import type { MatchVerdict } from "@synapse/engine";
import { Share } from "react-native";

interface ShareChallengeOptions {
  startWord: string;
  targetWord: string;
  playerPath?: string[];
  steps?: number;
  theme?: string;
  userGaveUp?: boolean;
  goldPath?: string[];
}

interface ShareDailyChallengeOptions {
  challengeId: string;
  startWord: string;
  targetWord: string;
  /** Her match verdict (spec §5) when she played this daily. */
  verdict?: MatchVerdict | null;
  /** She reached the target (so aiSteps is her finished count). */
  herFinished?: boolean;
  aiSteps: number;
  userSteps?: number;
  userCompleted?: boolean;
  userGaveUp?: boolean;
  challengeDate: string;
}

/**
 * Share a challenge with friends - simplified to copy link only
 */
export const shareChallenge = async ({
  startWord,
  targetWord,
  playerPath,
  steps,
  theme,
  userGaveUp,
  goldPath,
}: ShareChallengeOptions): Promise<boolean> => {
  try {
    // Generate the simple deep link
    const deepLink = generateSecureGameDeepLink(
      "challenge",
      startWord,
      targetWord,
      theme,
    );

    // Generate challenge message
    const challengeMessage = generateChallengeMessage({
      playerPath,
      steps,
      userGaveUp,
      goldPath,
    });

    // Web-specific sharing handling
    if (typeof window !== "undefined") {
      console.log("🎯 Web platform detected, checking sharing options...");

      // Check if Web Share API is supported
      if (typeof navigator !== "undefined" && navigator.share) {
        console.log("🎯 Web Share API available, trying...");
        try {
          await navigator.share({
            title: "Synapse Challenge",
            text: challengeMessage,
            url: deepLink,
          });
          console.log("🎯 Web Share API succeeded");
          return true;
        } catch (shareError) {
          console.log("🎯 Web Share API failed or cancelled:", shareError);
          // Fall through to manual copy
        }
      }

      // Manual copy for web
      console.log("🎯 Using manual copy fallback for web");
      try {
        await navigator.clipboard.writeText(
          `${challengeMessage}\n\n${deepLink}`,
        );
        console.log("🎯 Manual copy succeeded");
        console.log("✅ Challenge link copied to clipboard");
        return true;
      } catch (copyError) {
        console.error("🎯 Manual copy failed:", copyError);
        // Fallback: show the URL for manual copying
        const fullMessage = `${challengeMessage}\n\n${deepLink}`;
        console.log("📋 Please copy manually:", fullMessage);
        return true;
      }
    }

    // Mobile sharing
    try {
      const result = await Share.share({
        message: `${challengeMessage}\n\n${deepLink}`,
        url: deepLink,
        title: "Synapse Challenge",
      });

      return result.action === Share.sharedAction;
    } catch (error) {
      console.error("Error sharing challenge:", error);
      return false;
    }
  } catch (error) {
    console.error("Error in shareChallenge:", error);
    return false;
  }
};

/**
 * Share a daily challenge with friends - simplified to copy link only
 */
export const shareDailyChallenge = async ({
  challengeId,
  startWord,
  targetWord,
  verdict,
  herFinished,
  aiSteps,
  userSteps,
  userCompleted,
  userGaveUp,
  challengeDate,
}: ShareDailyChallengeOptions): Promise<boolean> => {
  try {
    console.log("🎯 shareDailyChallenge called with challenge:", challengeId);

    // Generate the daily challenge deep link
    const deepLink = generateSecureGameDeepLink(
      "dailychallenge",
      startWord,
      targetWord,
      undefined, // no theme for daily challenges
      challengeDate,
    );

    // Generate taunting message
    const tauntMessage = generateDailyChallengeTaunt({
      verdict,
      herFinished,
      aiSteps,
      userSteps,
      userCompleted,
      userGaveUp,
    });

    // Web-specific sharing handling
    if (typeof window !== "undefined") {
      console.log("🎯 Web platform detected, checking sharing options...");

      // Check if Web Share API is supported
      if (typeof navigator !== "undefined" && navigator.share) {
        console.log("🎯 Web Share API available, trying...");
        try {
          await navigator.share({
            title: "Synapse Daily Challenge",
            text: tauntMessage,
            url: deepLink,
          });
          console.log("🎯 Web Share API succeeded");
          return true;
        } catch (shareError) {
          console.log("🎯 Web Share API failed or cancelled:", shareError);
          // Fall through to manual copy
        }
      }

      // Manual copy for web
      console.log("🎯 Using manual copy fallback for web");
      try {
        await navigator.clipboard.writeText(`${tauntMessage}\n\n${deepLink}`);
        console.log("🎯 Manual copy succeeded");
        console.log("✅ Daily challenge link copied to clipboard");
        return true;
      } catch (copyError) {
        console.error("🎯 Manual copy failed:", copyError);
        // Fallback: show the URL for manual copying
        const fullMessage = `${tauntMessage}\n\n${deepLink}`;
        console.log("📋 Please copy daily challenge manually:", fullMessage);
        return true;
      }
    }

    // Mobile sharing
    try {
      const result = await Share.share({
        message: `${tauntMessage}\n\n${deepLink}`,
        url: deepLink,
        title: "Synapse Daily Challenge",
      });

      return result.action === Share.sharedAction;
    } catch (error) {
      console.error("Error sharing daily challenge:", error);
      return false;
    }
  } catch (error) {
    console.error("Error in shareDailyChallenge:", error);
    return false;
  }
};

/**
 * Generate a challenge message without complex features
 */
interface GenerateChallengeMessageParams {
  playerPath?: string[];
  steps?: number;
  userGaveUp?: boolean;
  goldPath?: string[];
}

export const generateChallengeMessage = ({
  playerPath,
  steps,
  userGaveUp,
  goldPath,
}: GenerateChallengeMessageParams): string => {
  // If player has a path (either completed or gave up)
  if (playerPath && playerPath.length > 1) {
    const pathLength = steps || playerPath.length - 1;
    const stepText = pathLength === 1 ? "step" : "steps";

    // If user gave up
    if (userGaveUp) {
      return `😅 I gave up on this one. Can you solve it?`;
    }

    // A perfect game: par moves (the gold path's hops, the fewest possible)
    const isPerfectGame =
      goldPath && goldPath.length > 0
        ? pathLength === goldPath.length - 1
        : false;

    if (isPerfectGame) {
      return `💫 I got a PERFECT game: ${pathLength} ${stepText}, the fewest possible! Can you match perfection?`;
    } else {
      return `💪 I solved it in ${pathLength} ${stepText}! Can you beat my score?`;
    }
  }

  return `🧠 Think you can solve this word connection challenge?`;
};

/**
 * Generate a daily challenge taunt message without complex features
 */
interface GenerateDailyChallengeTauntParams {
  /** Her match verdict (spec §5); without one the pre-Ariadne text is used. */
  verdict?: MatchVerdict | null;
  /** She reached the target (aiSteps is her finished count). */
  herFinished?: boolean;
  aiSteps: number;
  userSteps?: number;
  userCompleted?: boolean;
  userGaveUp?: boolean;
}

export const generateDailyChallengeTaunt = ({
  verdict,
  herFinished,
  aiSteps,
  userSteps,
  userCompleted,
  userGaveUp,
}: GenerateDailyChallengeTauntParams): string => {
  const u = userSteps ?? 0;
  const moves = (n: number) => `${n} ${n === 1 ? "move" : "moves"}`;
  if (verdict) {
    switch (verdict) {
      case "ahead":
        return `🧵 You ${u} · Ariadne ${aiSteps}. I was out of the labyrinth before she was. Your turn.`;
      case "tied":
        return `🧵 You ${u} · Ariadne ${aiSteps}. Step for step with Ariadne. Break the tie?`;
      case "behind":
        return `🧵 Ariadne ${aiSteps} · You ${u}. She beat me to the door. Can you do better?`;
      case "left_behind":
        return `🧵 Out in ${moves(u)} and Ariadne was still in the labyrinth. Can you lose her too?`;
      case "gave_up":
        return herFinished
          ? `😅 I gave up. Ariadne got there in ${aiSteps}. Think you can find the way out?`
          : "😅 I gave up. Ariadne is still in there somewhere. Think you can find the way out?";
    }
  }
  const stepText = aiSteps === 1 ? "step" : "steps";

  // If user completed it, compare with AI
  if (userCompleted && userSteps) {
    const userStepText = userSteps === 1 ? "step" : "steps";

    if (userSteps < aiSteps) {
      return `💪 I beat the AI! Got it in ${userSteps} ${userStepText} vs AI's ${aiSteps}. Think you can do better?`;
    } else if (userSteps === aiSteps) {
      return `🤝 I matched the AI! We both solved it in ${userSteps} ${userStepText}. Can you do better?`;
    } else {
      return `🤖 The AI beat me this time! I took ${userSteps} ${userStepText} vs AI's ${aiSteps}. Think you can do better?`;
    }
  } else if (userGaveUp) {
    return `😅 I gave up, but the AI solved it in ${aiSteps} ${stepText}. Think you can beat the AI?`;
  } else {
    return `🤖 The AI solved it in ${aiSteps} ${stepText}. Can you beat it?`;
  }
};

/**
 * Generate a non-authoritative URL hash.
 *
 * DECISION (2026-06-29): the hash is NOT validated on receive. The secret is
 * embedded in the shipped client bundle (no real security), and the dev
 * sticker generator uses a different hash variant — validating here would
 * reject valid sticker links for zero security benefit. The parameter is kept
 * only so older links keep parsing.
 */
export const generateUrlHash = (data: string): string => {
  // Simple hash function for URL validation (matches edge function)
  let hashValue = 0;
  const secret = "synapse_challenge_2025";
  const combined = data + secret;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hashValue = hashValue * 5 - hashValue + char;
    hashValue = hashValue % 2147483647; // Keep it positive 32-bit
  }
  return Math.abs(hashValue).toString(36).substring(0, 8);
};

/**
 * Generate a secure deep link with hash validation - simplified
 */
export const generateSecureGameDeepLink = (
  type: "challenge" | "dailychallenge",
  startWord: string,
  targetWord: string,
  theme?: string,
  challengeDate?: string, // For daily challenges, use date instead of internal ID
): string => {
  // Generate security hash - no user ID needed for sharing
  const challengeData = challengeDate
    ? `${challengeDate}:${startWord.toLowerCase()}:${targetWord.toLowerCase()}`
    : `${startWord.toLowerCase()}:${targetWord.toLowerCase()}`;

  const hash = generateUrlHash(challengeData);

  // Build clean parameters
  const params = new URLSearchParams();
  params.set("type", type);
  params.set("start", startWord);
  params.set("target", targetWord);
  params.set("hash", hash);

  if (theme) params.set("theme", theme);
  if (challengeDate) params.set("date", challengeDate);

  // Use the app's scheme for deep linking
  if (typeof window !== "undefined") {
    // Use current origin or a fixed URL for the web version
    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://synapse.gbe.games";
    return `${origin}/challenge?${params.toString()}`;
  }

  // For native apps, use the custom scheme
  return `synapse://challenge?${params.toString()}`;
};

/**
 * Parse enhanced game link to extract challenge parameters
 */
export const parseEnhancedGameLink = (
  url: string,
): {
  type?: string;
  startWord?: string;
  targetWord?: string;
  challengeId?: string;
  theme?: string;
  hash?: string;
} | null => {
  try {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;

    return {
      type: params.get("type") || undefined,
      startWord: params.get("start") || undefined,
      targetWord: params.get("target") || undefined,
      challengeId: params.get("id") || params.get("date") || undefined, // Support both id and date parameters
      theme: params.get("theme") || undefined,
      hash: params.get("hash") || undefined,
    };
  } catch (error) {
    console.error("🔗 parseEnhancedGameLink: Error parsing URL:", error);
    return null;
  }
};
